import { useState, useEffect, useCallback } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, addDays } from 'date-fns';
import { getPublishedRoster, getRosterPublicationStatus, RosterPublicationStatus } from '../../services/rosterService';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import RosterAvailabilityNotice from './RosterAvailabilityNotice';
import { getStaffShiftLabel } from '../../utils/rosterStyles';

type ViewMode = 'week' | 'fortnight' | 'month';

const shiftTheme: Record<string, string> = {
    CLEARER: 'bg-blue-50 text-blue-700 border-blue-200',
    LHR: 'bg-rose-50 text-rose-700 border-rose-200',
    CONTROL: 'bg-purple-50 text-purple-700 border-purple-200',
    OFFICE: 'bg-slate-50 text-slate-700 border-slate-200',
    WAREHOUSE: 'bg-amber-50 text-amber-700 border-amber-200',
};

const MyRoster = () => {
    const { user } = useAuth();
    const [anchorDate, setAnchorDate] = useState(new Date());
    const [viewMode, setViewMode] = useState<ViewMode>('week');
    const [rosterData, setRosterData] = useState<any>(null);
    const [publicationStatus, setPublicationStatus] = useState<RosterPublicationStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadRoster = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [data, status] = await Promise.all([
                getPublishedRoster(anchorDate.getFullYear(), anchorDate.getMonth() + 1),
                getRosterPublicationStatus(anchorDate.getFullYear(), anchorDate.getMonth() + 1),
            ]);
            setRosterData(data);
            setPublicationStatus(status);
        } catch (err: any) {
            setError(err.response?.data?.error?.message || err.message || 'Failed to load roster');
        } finally {
            setLoading(false);
        }
    }, [anchorDate]);

    useEffect(() => { loadRoster(); }, [loadRoster]);

    const visibleDays = (() => {
        if (viewMode === 'month') {
            return eachDayOfInterval({ start: startOfMonth(anchorDate), end: endOfMonth(anchorDate) });
        }
        const start = startOfWeek(anchorDate, { weekStartsOn: 1 });
        const length = viewMode === 'week' ? 7 : 14;
        return Array.from({ length }, (_, index) => addDays(start, index));
    })();

    const myRoster = rosterData?.staff?.find((member: any) => member.id === user?.id);
    const shiftsByDate = myRoster?.shifts || {};
    const versionStatus = rosterData?.version?.status;

    const moveBack = () => {
        if (viewMode === 'month') setAnchorDate(prev => subMonths(prev, 1));
        else setAnchorDate(prev => addDays(prev, viewMode === 'week' ? -7 : -14));
    };

    const moveForward = () => {
        if (viewMode === 'month') setAnchorDate(prev => addMonths(prev, 1));
        else setAnchorDate(prev => addDays(prev, viewMode === 'week' ? 7 : 14));
    };

    return (
        <div className="max-w-5xl mx-auto space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-2xl font-bold text-slate-800">My Roster</h1>
                        {versionStatus && (
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${versionStatus === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                {versionStatus}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{format(visibleDays[0], 'd MMM')} - {format(visibleDays[visibleDays.length - 1], 'd MMM yyyy')}</p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex bg-white border border-slate-200 rounded-lg p-1">
                        {(['week', 'fortnight', 'month'] as ViewMode[]).map(mode => (
                            <button
                                key={mode}
                                onClick={() => setViewMode(mode)}
                                className={`px-3 py-1.5 rounded-md text-sm font-semibold capitalize ${viewMode === mode ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                            >
                                {mode === 'fortnight' ? '2 weeks' : mode}
                            </button>
                        ))}
                    </div>
                    <div className="flex bg-white border border-slate-200 rounded-lg">
                        <button onClick={moveBack} className="p-2 text-slate-500 hover:bg-slate-100 rounded-l-lg" aria-label="Previous">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button onClick={moveForward} className="p-2 text-slate-500 hover:bg-slate-100 rounded-r-lg" aria-label="Next">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {loading && <div className="bg-white border border-slate-200 rounded-lg p-8 text-slate-500">Loading roster...</div>}
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>}

            {!loading && !error && publicationStatus && publicationStatus !== 'PUBLISHED' && (
                <RosterAvailabilityNotice status={publicationStatus} />
            )}

            {!loading && !error && publicationStatus === 'PUBLISHED' && (
                <div className="space-y-3">
                    {!myRoster && (
                        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">
                            No published roster entries found for your account in this period.
                        </div>
                    )}

                    {myRoster && visibleDays.map(day => {
                        const dateKey = format(day, 'yyyy-MM-dd');
                        const shift = shiftsByDate[dateKey];
                        const theme = shiftTheme[shift?.type] || 'bg-slate-50 text-slate-700 border-slate-200';
                        const isChanged = Boolean(shift?.assignmentChanged);
                        const needsCover = Boolean(shift?.needsCover);

                        return (
                            <div key={dateKey} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-semibold text-slate-900">{format(day, 'EEEE')}</p>
                                    <p className="text-sm text-slate-500">{format(day, 'd MMM yyyy')}</p>
                                </div>
                                {shift ? (
                                    <div className={`border rounded-lg px-3 py-2 text-right ${theme}`}>
                                        <p className="font-bold">{getStaffShiftLabel(shift)}</p>
                                        <p className="text-xs font-semibold opacity-80">Rostered shift</p>
                                        <div className="mt-1 flex flex-wrap justify-end gap-1">
                                            {needsCover && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">Needs cover</span>}
                                            {isChanged && <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold">{shift.assignmentChangeType}</span>}
                                            {shift.originalUser && shift.originalUser.id !== shift.userId && (
                                                <span className="px-1.5 py-0.5 rounded bg-white/70 text-slate-700 text-[10px] font-bold">Originally {shift.originalUser.name}</span>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-sm font-medium text-slate-400">Off</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyRoster;
